import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function HostObligationsPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Host Obligations & Standards
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.host_obligations}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-slate-600 mb-4">
              As a Host on Southwest Florida Vacation Rentals, you agree to maintain high standards 
              and comply with these obligations. Failure to meet these standards may result in 
              listing removal, account suspension, or other enforcement actions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. Listing Accuracy
            </h2>
            <p className="text-slate-600 mb-4">You must:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide accurate and complete property descriptions</li>
              <li>Use current, authentic photos that accurately represent the property</li>
              <li>Disclose all relevant information about the property and amenities</li>
              <li>Update listings promptly when conditions change</li>
              <li>Accurately list the number of bedrooms, bathrooms, and maximum occupancy</li>
              <li>Only list properties located in Southwest Florida</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. Property Standards
            </h2>
            <p className="text-slate-600 mb-4">Your property must:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Be clean and sanitary for each guest arrival</li>
              <li>Have all advertised amenities in working condition</li>
              <li>Meet all local safety codes and regulations</li>
              <li>Have working smoke detectors and carbon monoxide detectors</li>
              <li>Provide clean linens, towels, and essential supplies</li>
              <li>Be free of hazards that could endanger guests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Legal Compliance
            </h2>
            <p className="text-slate-600 mb-4">Hosts are responsible for:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Obtaining all necessary permits and licenses for short-term rentals</li>
              <li>Complying with local zoning laws and HOA regulations</li>
              <li>Collecting and remitting required occupancy taxes (where applicable)</li>
              <li>Maintaining adequate liability insurance</li>
              <li>Adhering to fair housing laws and non-discrimination requirements</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> SWFL Rentals does not verify permits or licenses. 
                It is solely the Host's responsibility to ensure legal compliance.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. Communication & Responsiveness
            </h2>
            <p className="text-slate-600 mb-4">Hosts must:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Respond to booking inquiries within 24 hours</li>
              <li>Be reachable for guest questions during their stay</li>
              <li>Provide clear check-in instructions before arrival</li>
              <li>Address guest concerns and issues promptly</li>
              <li>Communicate any property changes or access issues immediately</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Booking Commitments
            </h2>
            <p className="text-slate-600 mb-4">You agree to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Honor all confirmed bookings</li>
              <li>Not cancel bookings except in genuine emergencies</li>
              <li>Maintain accurate availability calendars</li>
              <li>Not request payment outside the platform</li>
              <li>Provide alternative accommodation or full refund if you must cancel</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. Pricing Transparency
            </h2>
            <p className="text-slate-600 mb-4">
              All pricing must be accurately reflected in your listing:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Nightly rates should reflect the true cost of accommodation</li>
              <li>Cleaning fees must be reasonable and clearly disclosed</li>
              <li>No hidden fees or surprise charges upon arrival</li>
              <li>Security deposits (if any) must be clearly stated</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Non-Discrimination
            </h2>
            <p className="text-slate-600 mb-4">
              Hosts must not discriminate against guests based on:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Race, color, ethnicity, or national origin</li>
              <li>Religion</li>
              <li>Sex, gender identity, or sexual orientation</li>
              <li>Disability</li>
              <li>Marital or family status</li>
              <li>Age</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              9. Payout Requirements
            </h2>
            <p className="text-slate-600 mb-4">
              To receive payouts, Hosts must:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Complete Stripe Connect onboarding</li>
              <li>Provide accurate banking and tax information</li>
              <li>Maintain an active and verified payment account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              10. Enforcement
            </h2>
            <p className="text-slate-600 mb-4">
              Violations of these standards may result in:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Warning notices</li>
              <li>Temporary listing suspension</li>
              <li>Permanent listing removal</li>
              <li>Account suspension or termination</li>
              <li>Withholding of payouts pending investigation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              11. Contact
            </h2>
            <p className="text-slate-600">
              For questions about Host obligations, please contact us at:<br />
              Email: hosts@southwestflvacationrentals.com
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
